import { useEffect, useRef } from "react";

export type GlobalNavigationTarget = {
  scope: string;
  section?: string;
  subsection?: string;
  anchor?: string;
};

const STORAGE_KEY = "inpronexus.global-search.navigation-target";
const EVENT_NAME = "inpronexus:global-search:navigate";

function isNavigationTarget(value: unknown): value is GlobalNavigationTarget {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<GlobalNavigationTarget>;
  return typeof candidate.scope === "string" && candidate.scope.trim() !== "";
}

function readQueuedTarget(): GlobalNavigationTarget | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    return isNavigationTarget(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function queueGlobalNavigation(target: GlobalNavigationTarget): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  } catch {
    // La navegación continúa aunque el almacenamiento de sesión no esté disponible.
  }
}

export function notifyGlobalNavigation(target: GlobalNavigationTarget): void {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<GlobalNavigationTarget>(EVENT_NAME, { detail: target }),
  );
}

function consumeQueuedTarget(scope: string): GlobalNavigationTarget | null {
  const target = readQueuedTarget();
  if (!target || target.scope !== scope) return null;

  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // No bloqueamos la navegación si sessionStorage no está disponible.
  }

  return target;
}

export function useGlobalNavigationTarget(
  scope: string,
  onTarget: (target: GlobalNavigationTarget) => void,
): void {
  const handlerRef = useRef(onTarget);
  handlerRef.current = onTarget;

  useEffect(() => {
    const queued = consumeQueuedTarget(scope);
    if (queued) {
      handlerRef.current(queued);
    }

    const onNavigation = (event: Event) => {
      const customEvent = event as CustomEvent<GlobalNavigationTarget>;
      const target = customEvent.detail;
      if (!isNavigationTarget(target) || target.scope !== scope) return;

      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // La aplicación del destino no depende del almacenamiento de sesión.
      }

      handlerRef.current(target);
    };

    window.addEventListener(EVENT_NAME, onNavigation);
    return () => window.removeEventListener(EVENT_NAME, onNavigation);
  }, [scope]);
}
